import { localStorageStore, type Store } from 'react-admin';
import { getFlagsSnapshot } from '../featureFlags/featureFlagsApi';
import { sessionUserId } from '../../authProvider';

/**
 * Filtry list zapamiętywane automatycznie, osobno dla każdego konta
 * (decyzja użytkownika 19.10.2026: „dobrze by było móc zapisywać filtry,
 * ale powinno się to robić automatycznie, czyli można ukryć”).
 *
 * ## Jak było
 *
 * React-admin trzyma ostatnie parametry każdej listy (filtry, sortowanie,
 * liczbę na stronie) w magazynie `localStorageStore()` pod kluczem
 * `RaStore.<zasób>.listParams` (albo `storeKey` listy). Przetrwają przejście
 * między ekranami i przeładowanie, ale:
 * - magazyn jest wspólny dla przeglądarki — na wspólnym komputerze kolejne
 *   konto dostaje filtry poprzedniego, jeśli ten zamknął kartę bez
 *   wylogowania;
 * - wylogowanie (i wygasła sesja) czyści cały magazyn — po zalogowaniu
 *   każda lista startuje od zera;
 * - „Zapisz obecne filtry” w „Dodaj filtr” to ręczne zapisane zapytania,
 *   też we wspólnym magazynie.
 *
 * ## Z przełącznikiem `autoListFilters`
 *
 * Klucze magazynu dostają identyfikator konta (`RaUserStore.<id>.<klucz>`),
 * więc dwa konta na jednym komputerze nie widzą swoich filtrów. Wylogowanie
 * nie czyści ich (nie ma po co — nie przeciekają), tylko zaznaczenia wierszy
 * i wspólne klucze, jak dotąd. Zapisane zapytania react-admina są
 * niewidoczne — ich przycisk ukrywa `AutoListFilters`. Klucze filtrów
 * (`source="…"`) i klucze list (`storeKey`) bez zmian — to dane.
 *
 * Bez przełącznika (i zanim przyjdą przełączniki po logowaniu) magazyn
 * działa dokładnie jak `localStorageStore()` react-admina.
 */

/** Klucz przełącznika — jak w API (`feature-flags.ts`), nie przemianowujemy. */
export const FEATURE_AUTO_LIST_FILTERS = 'autoListFilters';

/** Przedrostek kluczy react-admina (`localStorageStore`) — wspólny dla przeglądarki. */
const SHARED_PREFIX = 'RaStore';

/**
 * Przedrostek kluczy konta. Celowo nie zaczyna się od `RaStore` — reset
 * magazynu react-admina (wylogowanie przy wyłączonym przełączniku) usuwa
 * wszystko, co się od niego zaczyna.
 */
export const USER_PREFIX = 'RaUserStore';

/**
 * Klucze wspólne dla przeglądarki także przy przełączniku: stan panelu
 * bocznego i ustawienia wyglądu. Panel rysuje się, zanim przyjdą
 * przełączniki, więc z kluczem konta czytałby raz jedno, raz drugie miejsce.
 */
const SHARED_KEYS: ReadonlySet<string> = new Set(['sidebar.open', 'locale', 'theme', 'themeName']);

/** Zaznaczenia wierszy — czyszczone przy wylogowaniu jak dotąd. */
const isSelectionKey = (key: string) => /\.selectedIds(\.|$)/.test(key);

/** Ręcznie zapisane zapytania react-admina — przy przełączniku niewidoczne. */
const isSavedQueriesKey = (key: string) => key.endsWith('.savedQueries');

const tryParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const browserStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Konto, któremu przypisujemy klucze, albo `null` (zachowanie
 * react-admina): przełącznik wyłączony, przełączniki jeszcze się wczytują,
 * brak sesji.
 */
export function listMemoryScope(): string | null {
  const snapshot = getFlagsSnapshot();
  if (snapshot.status !== 'ready' || snapshot.state?.flags[FEATURE_AUTO_LIST_FILTERS] !== true) {
    return null;
  }
  const id = sessionUserId();
  return id === undefined ? null : String(id);
}

export type ListMemoryStoreOptions = {
  /** Identyfikator konta albo `null` — czytany przy każdym dostępie. */
  scope?: () => string | null;
  /** Magazyn przeglądarki dla kluczy konta (testy podstawiają własny). */
  storage?: () => Storage | null;
  /** Magazyn react-admina dla zachowania bez przełącznika. */
  shared?: Store;
};

type Subscription = { key: string; callback: (value: unknown) => void };

/**
 * Magazyn dla `<Admin store>`. Subskrypcje trzymamy po kluczu logicznym
 * (tym, którego używa react-admin), więc komponent zapisany przed zmianą
 * trybu dalej dostaje powiadomienia.
 */
export function createListMemoryStore(options: ListMemoryStoreOptions = {}): Store {
  const scopeOf = options.scope ?? listMemoryScope;
  const storageOf = options.storage ?? browserStorage;
  const shared = options.shared ?? localStorageStore();
  const subscriptions = new Map<number, Subscription>();
  let nextId = 0;

  const publish = (matches: (key: string) => boolean, value: unknown) => {
    Array.from(subscriptions.values()).forEach((s) => {
      if (matches(s.key)) s.callback(value);
    });
  };

  /** Tryb konta: identyfikator i magazyn; `null` = zachowanie react-admina. */
  const userMode = (key?: string): { prefix: string; storage: Storage } | null => {
    if (key !== undefined && SHARED_KEYS.has(key)) return null;
    const scope = scopeOf();
    if (!scope) return null;
    const storage = storageOf();
    return storage ? { prefix: `${USER_PREFIX}.${scope}.`, storage } : null;
  };

  const storageKeys = (storage: Storage): string[] => {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k !== null) keys.push(k);
    }
    return keys;
  };

  // Zmiana w innej karcie tej samej przeglądarki — jak w `localStorageStore`.
  const onStorage = (event: StorageEvent) => {
    if (!event.key) return;
    const mode = userMode();
    let key: string | null = null;
    if (mode && event.key.startsWith(mode.prefix)) {
      key = event.key.substring(mode.prefix.length);
    } else if (event.key.startsWith(`${SHARED_PREFIX}.`)) {
      key = event.key.substring(SHARED_PREFIX.length + 1);
      // Klucz konta czytamy z magazynu konta, nie wspólnego.
      if (userMode(key)) return;
    }
    if (key === null) return;
    const value = event.newValue == null ? undefined : tryParse(event.newValue);
    publish((k) => k === key, value ?? undefined);
  };

  return {
    setup: () => {
      shared.setup();
      if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    },
    teardown: () => {
      shared.teardown();
      if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
    },
    getItem<T = unknown>(key: string, defaultValue?: T): T | undefined {
      const mode = userMode(key);
      if (!mode) return shared.getItem<T>(key, defaultValue);
      if (isSavedQueriesKey(key)) return defaultValue;
      const raw = mode.storage.getItem(mode.prefix + key);
      return raw == null ? defaultValue : (tryParse(raw) as T);
    },
    setItem<T = unknown>(key: string, value: T): void {
      const mode = userMode(key);
      if (!mode) {
        shared.setItem(key, value);
      } else if (value === undefined) {
        mode.storage.removeItem(mode.prefix + key);
      } else {
        mode.storage.setItem(mode.prefix + key, JSON.stringify(value));
      }
      publish((k) => k === key, value);
    },
    removeItem(key: string): void {
      const mode = userMode(key);
      if (!mode) shared.removeItem(key);
      else mode.storage.removeItem(mode.prefix + key);
      publish((k) => k === key, undefined);
    },
    removeItems(keyPrefix: string): void {
      const mode = userMode();
      if (!mode) {
        shared.removeItems(keyPrefix);
      } else {
        storageKeys(mode.storage)
          .filter((k) => k.startsWith(mode.prefix + keyPrefix))
          .forEach((k) => mode.storage.removeItem(k));
      }
      publish((k) => k.startsWith(keyPrefix), undefined);
    },
    /**
     * Wylogowanie. Wspólny magazyn react-admina — cały, jak dotąd.
     * Z kluczy kont znikają tylko zaznaczenia wierszy; filtry, sortowanie
     * i liczba na stronie zostają (klucz ma konto, więc nie przeciekają).
     * Przy wylogowaniu sesji już nie ma, więc działa to także wtedy, gdy
     * tryb konta jest w tej chwili nieczynny.
     */
    reset(): void {
      const mode = userMode();
      shared.reset();
      const storage = storageOf();
      if (storage) {
        storageKeys(storage)
          .filter((k) => k.startsWith(`${USER_PREFIX}.`) && isSelectionKey(k))
          .forEach((k) => storage.removeItem(k));
      }
      publish(mode ? (k) => SHARED_KEYS.has(k) || isSelectionKey(k) : () => true, undefined);
    },
    subscribe: (key: string, callback: (value: unknown) => void) => {
      const id = nextId;
      nextId += 1;
      subscriptions.set(id, { key, callback });
      return () => {
        subscriptions.delete(id);
      };
    },
    listItems: (keyPrefix?: string) => {
      const mode = userMode();
      if (!mode) return shared.listItems ? shared.listItems(keyPrefix) : {};
      const full = mode.prefix + (keyPrefix ?? '');
      const items: Record<string, unknown> = {};
      storageKeys(mode.storage)
        .filter((k) => k.startsWith(full))
        .forEach((k) => {
          const key = k.substring(mode.prefix.length);
          if (isSavedQueriesKey(key)) return;
          const raw = mode.storage.getItem(k);
          if (raw != null) items[key] = tryParse(raw);
        });
      return items;
    },
  };
}

/** Magazyn aplikacji — jeden na kartę przeglądarki. */
export const listMemoryStore = createListMemoryStore();
